// useAssignmentBoardModel.js — 1:1 배정 보드 계산: 기준 달·진행 중 배정·마케터 목록·숨김(이 기기 저장)·배정 안 된 직원·건수
//   2026-10-06 AssignmentBoard.jsx 에서 글자 그대로 옮겼다(동작 변화 0). PC 보드와 휴대폰 화면이 같이 쓴다.
import { useMemo, useState } from 'react';
import { monthRange, getStatus } from './assignmentStatus';

export function hiddenKey(companyCode) {
  return `mc_hidden_${companyCode}`;
}

export function loadHidden(companyCode) {
  try {
    return new Set(JSON.parse(localStorage.getItem(hiddenKey(companyCode)) || '[]').map(Number));
  } catch {
    return new Set();
  }
}

export function saveHidden(companyCode, hiddenIds) {
  localStorage.setItem(hiddenKey(companyCode), JSON.stringify([...hiddenIds]));
}

export function isWorkbenchRow(row, range) {
  return getStatus(row, range).key !== 'ended';
}

export function buildMarketers(marketers, assignments) {
  const map = new Map(marketers.map((marketer) => [Number(marketer.id), { ...marketer, id: Number(marketer.id) }]));
  for (const row of assignments) {
    const id = Number(row.marketer_user_id);
    if (!map.has(id)) map.set(id, { id, name: row.marketer_name || `마케터 ${id}` });
  }
  return [...map.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko'));
}

export function buildUnassignedConsultants(consultants, currentAssignments) {
  const assignedIds = new Set(currentAssignments.map((row) => Number(row.consultant_user_id)));
  return consultants
    .filter((user) => !assignedIds.has(Number(user.id)))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko'));
}

export function useAssignmentBoardModel({ marketers, consultants = [], assignments, today, companyCode, basisMonth }) {
  const [hiddenIds, setHiddenIds] = useState(() => loadHidden(companyCode));
  const [companyForHidden, setCompanyForHidden] = useState(companyCode);

  if (companyForHidden !== companyCode) {
    setCompanyForHidden(companyCode);
    setHiddenIds(loadHidden(companyCode));
  }

  const billingRange = useMemo(() => monthRange(basisMonth, today), [basisMonth, today]);
  const currentAssignments = useMemo(
    () => assignments.filter((row) => isWorkbenchRow(row, billingRange)),
    [assignments, billingRange],
  );
  const allMarketers = useMemo(() => buildMarketers(marketers, assignments), [marketers, assignments]);
  const visibleMarketers = allMarketers.filter((marketer) => !hiddenIds.has(marketer.id));
  const hiddenMarketers = allMarketers.filter((marketer) => hiddenIds.has(marketer.id));
  const unassignedConsultants = useMemo(() => buildUnassignedConsultants(consultants, currentAssignments), [consultants, currentAssignments]);
  const stats = currentAssignments.reduce((acc, row) => {
    acc[getStatus(row, billingRange).key] += 1;
    return acc;
  }, { active: 0, upcoming: 0, ended: 0 });

  function toggleHide(id) {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveHidden(companyCode, next);
      return next;
    });
  }

  return {
    hiddenIds,
    billingRange,
    currentAssignments,
    allMarketers,
    visibleMarketers,
    hiddenMarketers,
    unassignedConsultants,
    stats,
    toggleHide,
  };
}
